import axios, { AxiosInstance } from 'axios';
import { env } from '../config/env';
import { redirectToCustomerLogin } from '../lib/sso';

const createClient = (redirectOn401: boolean): AxiosInstance => {
  const client = axios.create({
    baseURL: env.AUTH_API_URL,
    headers: { 'Content-Type': 'application/json' },
  });
  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('inference_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (redirectOn401 && error.response?.status === 401) {
        localStorage.removeItem('inference_token');
        redirectToCustomerLogin();
      }
      return Promise.reject(error);
    }
  );
  return client;
};

const authApi = createClient(true);
// Login-page calls must not redirect-loop on 401.
const safeAuthApi = createClient(false);

export const authService = {
  requestOTP: (email: string) => safeAuthApi.post('/auth/login', { email }),
  verifyOTP: (email: string, otp: string) => safeAuthApi.post('/auth/verify-otp', { email, otp }),
  googleLogin: (code: string, redirectUri: string) =>
    safeAuthApi.post('/auth/google', { code, redirect_uri: redirectUri }),
  getProfile: () => authApi.get('/auth/profile'),
  logout: () => authApi.post('/auth/logout'),
};

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  expires_at?: string;
}

// Response of POST /api-tokens - `token` is the full secret, shown once.
export interface CreatedApiToken {
  token: string;
  id: string;
  name: string;
  prefix: string;
  expires_at?: string;
}

// expires_in accepted by the auth service: 7d|30d|90d|180d|365d|never.
export type TokenExpiry = '7d' | '30d' | '90d' | '180d' | '365d' | 'never';

export const apiTokenService = {
  list: () => authApi.get<{ data: ApiToken[] }>('/api-tokens'),
  create: (name: string, expiresIn: TokenExpiry) =>
    authApi.post<CreatedApiToken>('/api-tokens', { name, expires_in: expiresIn }),
  revoke: (id: string) => authApi.delete(`/api-tokens/${id}`),
};
